import { getAllergyIntolerancesById } from '@ctw/shared/api/fhir/allergies';
import { getCareTeamMembersByIdFromFQS } from '@ctw/shared/api/fhir/care-team';
import { fetchClaimsByIdFQS } from '@ctw/shared/api/fhir/claims';
import { fetchConditionsByIdFQS } from '@ctw/shared/api/fhir/conditions';
import { fetchCoveragesByIdFQS } from '@ctw/shared/api/fhir/coverages';
import { fetchDiagnosticReportsFromFQSById } from '@ctw/shared/api/fhir/diagnostic-report';
import { getDocumentReferenceWithBasicsById } from '@ctw/shared/api/fhir/document';
import { getEncountersById } from '@ctw/shared/api/fhir/encounters';
import { getImmunizationsFromFQS } from '@ctw/shared/api/fhir/immunizations';
import { getMedicationStatementsByIdFQS } from '@ctw/shared/api/fhir/medications';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import { fetchObservationsById } from '@ctw/shared/api/fhir/observations';
import { getPatientByID } from '@ctw/shared/api/fhir/patient-helper';
import { fetchProceduresById } from '@ctw/shared/api/fhir/procedure';
import { fetchReferralsByIdFQS } from '@ctw/shared/api/fhir/referrals';
import { getRiskDiagnosesByIds } from '@ctw/shared/api/fhir/risk-diagnosis';
import { getRiskGapsByIds } from '@ctw/shared/api/fhir/risk-gaps';
import type { ResourceTypeString } from '@ctw/shared/api/fhir/types';
import type { FqsRequestOptions } from '@ctw/shared/api/fqs/client';
import { getBinariesByIds } from '@ctw/shared/api/fqs-rest/binaries';
import type { CTWRequestContext, CTWState } from '@ctw/shared/context/ctw-context';
import type { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useCtwQuery } from '@ctw/shared/hooks/use-ctw-query';
import type {
  FetchableModelType,
  FetchableResourceType,
  ResourceTypeModelMap,
} from '@ctw/shared/utils/fhir/resource-mappings';
import { QUERY_KEY_PATIENT_RESOURCES_BY_TYPES_AND_IDS } from '@ctw/shared/utils/query-keys';
import type { NonEmptyArray } from '@ctw/shared/utils/types';
import type { QueryClient } from '@tanstack/query-core';
import { useQueryClient } from '@tanstack/react-query';
import type { GraphQLClient } from 'graphql-request';
import { flatten, groupBy } from 'lodash-es';

interface UseResourcesOptions {
  identifiers: Array<FetchableResource>;
  patient: PatientModel;
}

export const useResources = <TModel extends FetchableModelType<FetchableResourceType>>({
  identifiers,
  patient,
}: UseResourcesOptions) => {
  const queryClient = useQueryClient();
  const fqsHotstorageRequestOptions = useFqsHotstorageRequestOptions();
  return useCtwQuery<Array<TModel>>({
    queryId: QUERY_KEY_PATIENT_RESOURCES_BY_TYPES_AND_IDS,
    queryKey: identifiers
      .map((identifier) => identifier.resourceId)
      .sort((left, right) => left.localeCompare(right)),
    queryFn: async ({ ctwFetch, requestContext, telemetry, graphqlClient }) => {
      const identifiersByResourceType = groupBy(identifiers, 'resourceType');
      const resourceTypeQueryPromises = Object.entries(identifiersByResourceType).map(
        ([resourceType, identifiersForResourceType]) =>
          new Promise<Array<TModel>>(
            (resolve) =>
              void fetchResourceByTypeAndIds({
                ctwFetch,
                requestContext,
                graphqlClient,
                queryClient,
                telemetry,
                patient,
                resourceType: resourceType as FetchableResourceType,
                resourceIds: identifiersForResourceType.map(
                  (identifier) => identifier.resourceId,
                ) as NonEmptyArray<string>,
                fqsHotstorageRequestOptions,
              })
                .then((models) => resolve(models as Array<TModel>))
                .catch(() => resolve([])),
          ),
      );

      return Promise.allSettled(resourceTypeQueryPromises).then((results) =>
        flatten(results.map((result) => (result.status === 'fulfilled' ? result.value : []))),
      );
    },
  });
};

// The X-Use-Hotstorage header only applies to DocumentReference, DiagnosticReport,
// Observation, and Encounter FQS queries. All four are permanently on hotstorage; none
// are feature-flag-gated.
type HotstorageResourceType =
  | 'DocumentReference'
  | 'DiagnosticReport'
  | 'Observation'
  | 'Encounter';

export type FqsHotstorageRequestOptions = Partial<
  Record<HotstorageResourceType, FqsRequestOptions>
>;

// Hook to be called from any React context that drives an FQS resource lookup for
// a hotstorage-flagged resource type. Returns a per-resource-type options map that
// can be threaded into `fetchResourceByTypeAndIds` (or used directly when calling
// the per-resource fetchers).
export function useFqsHotstorageRequestOptions(): FqsHotstorageRequestOptions {
  return {
    DocumentReference: {
      requestHeaders: {
        'X-Use-Hotstorage': '1',
      },
    },
    DiagnosticReport: {
      requestHeaders: {
        'X-Use-Hotstorage': '1',
      },
    },
    Observation: {
      requestHeaders: {
        'X-Use-Hotstorage': '1',
      },
    },
    Encounter: {
      requestHeaders: {
        'X-Use-Hotstorage': '1',
      },
    },
  };
}

export interface FetchResourceByTypeAndIdsOptions<TResourceType extends FetchableResourceType> {
  resourceType: TResourceType;
  resourceIds: NonEmptyArray<string>;
  telemetry: ReturnType<typeof useTelemetry>;
  ctwFetch: CTWState['ctwFetch'];
  graphqlClient: GraphQLClient;
  queryClient: QueryClient;
  requestContext: CTWRequestContext;
  patient: PatientModel;
  includeClinicalNotes?: boolean;
  fqsHotstorageRequestOptions?: FqsHotstorageRequestOptions;
}

export const fetchResourceByTypeAndIds = async <
  TResourceType extends FetchableResourceType,
  TModel extends FetchableModelType<TResourceType>,
>({
  resourceType,
  resourceIds,
  telemetry,
  ctwFetch,
  graphqlClient,
  patient,
  requestContext,
  includeClinicalNotes,
  fqsHotstorageRequestOptions,
}: FetchResourceByTypeAndIdsOptions<TResourceType>): Promise<Array<TModel>> => {
  switch (resourceType) {
    case 'Claim':
      return (await fetchClaimsByIdFQS(
        telemetry,
        graphqlClient,
        patient,
        resourceIds,
      )) as Array<TModel>;
    case 'Condition':
      return (await fetchConditionsByIdFQS(
        telemetry,
        graphqlClient,
        patient,
        resourceIds,
      )) as Array<TModel>;
    case 'Immunization':
      return (await getImmunizationsFromFQS(
        telemetry,
        graphqlClient,
        requestContext,
        patient,
        resourceIds,
      )) as Array<TModel>;
    case 'AllergyIntolerance':
      return (await getAllergyIntolerancesById(
        telemetry,
        graphqlClient,
        patient,
        resourceIds,
      )) as Array<TModel>;
    case 'Encounter':
      return (await getEncountersById(
        telemetry,
        graphqlClient,
        patient,
        resourceIds,
        includeClinicalNotes,
        fqsHotstorageRequestOptions?.Encounter,
      )) as Array<TModel>;
    case 'ExplanationOfBenefit':
      return (await getRiskDiagnosesByIds({
        ctwFetch,
        requestContext,
        ids: resourceIds,
      })) as Array<TModel>;
    case 'DiagnosticReport':
      return (await fetchDiagnosticReportsFromFQSById(
        telemetry,
        graphqlClient,
        patient,
        resourceIds,
        fqsHotstorageRequestOptions?.DiagnosticReport,
      )) as Array<TModel>;
    case 'ServiceRequest':
      return (await fetchReferralsByIdFQS(
        telemetry,
        graphqlClient,
        patient,
        resourceIds,
        requestContext.env,
      )) as Array<TModel>;
    case 'MedicationStatement':
      return (await getMedicationStatementsByIdFQS(
        telemetry,
        graphqlClient,
        patient,
        resourceIds,
      )) as Array<TModel>;
    case 'Practitioner':
      return (await getCareTeamMembersByIdFromFQS(
        telemetry,
        graphqlClient,
        patient,
        resourceIds,
      )) as Array<TModel>;
    case 'DocumentReference':
      return (await getDocumentReferenceWithBasicsById(
        telemetry,
        graphqlClient,
        patient,
        resourceIds,
        fqsHotstorageRequestOptions?.DocumentReference,
      )) as Array<TModel>;
    case 'Observation':
      return (await fetchObservationsById(
        telemetry,
        graphqlClient,
        patient,
        resourceIds,
        fqsHotstorageRequestOptions?.Observation,
      )) as Array<TModel>;
    case 'Binary':
      return (await getBinariesByIds({
        binaryIds: resourceIds,
        ctwFetch,
        requestContext,
      })) as Array<TModel>;
    case 'MeasureReport': {
      return (await getRiskGapsByIds({
        ctwFetch,
        requestContext,
        ids: resourceIds,
      })) as Array<TModel>;
    }
    case 'Patient': {
      const patients = await Promise.all(
        resourceIds.map((id) => getPatientByID(ctwFetch, requestContext, id)),
      );
      return patients as Array<TModel>;
    }
    case 'Coverage':
      return (await fetchCoveragesByIdFQS(
        telemetry,
        graphqlClient,
        patient,
        resourceIds,
      )) as Array<TModel>;
    case 'Procedure':
      return (await fetchProceduresById(
        telemetry,
        graphqlClient,
        patient,
        resourceIds,
      )) as Array<TModel>;
    default:
      return [];
  }
};

export interface FetchCanonicalCitationResourceArgs<TResourceType extends FetchableResourceType>
  extends FetchResourceByTypeAndIdsOptions<TResourceType> {}

export const fetchCanonicalCitationResource = async <
  TResourceType extends FetchableResourceType,
  TModel extends FetchableModelType<TResourceType>,
>(
  fetchResourceArgs: FetchCanonicalCitationResourceArgs<TResourceType>,
): Promise<TModel | null> => {
  return fetchResourceByTypeAndIds(fetchResourceArgs).then((models) => {
    const model = models[0];

    if (!model) {
      return null;
    }

    return model as TModel;
  });
};

export type FhirModelSubclassType<TResourceType extends ResourceTypeString> =
  TResourceType extends keyof ResourceTypeModelMap ? ResourceTypeModelMap[TResourceType] : never;

export interface FetchableResourceIdentifier {
  resourceId: string;
  resourceType: string;
}

export interface FetchableResource {
  resourceId: string;
  resourceType: FetchableResourceType;
}
